import moment from 'moment';
import { Link } from 'react-router-dom';
import { constants, pipelineColumnLabels, pipelineTooltipText } from '../../../../constants';
import { TransactionType } from '../../../../types/amr-pipeline/enums/TransactionType';
import { KMatrix } from '../../../../types/amr-pipeline/models/K-MatrixResponse';
import {
    canViewTransactionDetails,
    getKMatrixClassValue,
    getTransactionDetailsUrl,
} from '../../../../utils/amr-pipeline.utils';
import { IColumnDefinition } from '../../../bidding/common/table/types/ColumnDefinition';
import { OnHoverTooltip } from '../../../common';
import { ImSubscriptionActionBlocker } from '../../subscription/ImSubscriptionActionBlocker';
import { PipelineColumn } from '../../types/PipelineColumn';
import { DealUpdatesPopover } from '../DealUpdatesPopover';
import { ManagerLink } from '../../common/ManagerLink';
import { BankLink } from '../../common/BankLink';
import { startCase } from 'lodash';
import IconSVG from '../../../../styles/svg-icons';
import { dateTimeUtils, formatUtils, moneyUtils, numericUtils } from '../../../../utils';
import { ColumnBuilder } from '../../../bidding/common/table/columns/column-builder/ColumnBuilder';
import { RatingsWithLabels, Rating } from '../../../../types/enums/Rating';
import { OriginatingTransactionClass } from '../../../../types/amr-pipeline/models/OriginatingTransactionClass';
import { Popover } from '../../../controls';
import { ImSubscriptionValue } from '../../subscription/ImSubscriptionValue';
import { useMemo } from 'react';
import { kMatrixValuesColumnsConfigs } from '../../../../constants/amr-pipeline/KMatrixColumnConfigs';
import { Currency } from '../../../../types/enums/Currency';
import { NotApplicable } from '../../common/NotApplicable';
import { TransactionStatus, transactionStatusTitles } from '../../../../types/amr-pipeline/enums/TransactionStatus';
import StatusLabel from '../../StatusLabel';
import { KMatrixViewType } from '../../../../types/state/AmrDealsState';


const dmTooltip = "Includes guidance, status, and subscription for active deals; and DM, coupon, and price for priced and closed deals.";

function getKMatrixColumns(viewType: KMatrixViewType): IColumnDefinition<KMatrix>[] {
    return [{
        columnKey: PipelineColumn.lastUpdated,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.lastUpdated],
        renderColumnContent: transaction => (
            <ImSubscriptionActionBlocker>
                {blocked => {
                    const {
                        transactionReferenceName,
                        dealReferenceName,
                        dealLegalName,
                        lastUpdated,
                        type,
                        status,
                        version,
                        dealUpdates,
                    } = transaction;

                    return (
                        <DealUpdatesPopover
                            disabled={blocked}
                            referenceName={transactionReferenceName}
                            dealReferenceName={dealReferenceName}
                            dealLegalName={dealLegalName}
                            lastUpdated={lastUpdated}
                            type={type}
                            status={status}
                            version={version}
                            dealUpdates={dealUpdates}
                        />
                    );
                }}
            </ImSubscriptionActionBlocker>
        ),
        sortingField: PipelineColumn.lastUpdated,
        className: 'data-list-cell-sm',
        stickLeft: true,
    }, {
        columnKey: PipelineColumn.dealName,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.dealName],
        renderColumnContent: transaction => {
            const { dealLegalName, type, transactionReferenceName, dealReferenceName } = transaction;

            return (
                <OnHoverTooltip overlay={dealLegalName}>
                    {type === TransactionType.amr || canViewTransactionDetails() ? (
                        <Link
                            target="_blank"
                            to={getTransactionDetailsUrl(type, transactionReferenceName, dealReferenceName)}
                            className="link-filter"
                        >
                            {dealLegalName}
                        </Link>
                    ) : (
                        dealLegalName
                    )}
                </OnHoverTooltip>
            );
        },
        sortingField: PipelineColumn.dealName,
        className: 'data-list-cell-lg-02',
        stickLeft: true,
    }, {
        columnKey: PipelineColumn.manager,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.manager],
        renderColumnContent: transaction => {
            const { managerReferenceName, managerLegalName } = transaction;

            if (!managerReferenceName) {
                return constants.emptyPlaceholder;
            }

            return <ManagerLink referenceName={managerReferenceName} legalName={managerLegalName} />;
        },
        sortingField: 'collateralManager',
        className: 'data-list-cell-sm',
    }, {
        columnKey: PipelineColumn.arranger,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.arranger],
        renderColumnContent: transaction => {
            const { arrangerReferenceName, arrangerLegalName, arrangerCode } = transaction;

            if (!arrangerReferenceName) {
                return constants.emptyPlaceholder;
            }

            return (
                <BankLink
                    legalName={arrangerCode || arrangerLegalName}
                    referenceName={arrangerReferenceName}
                    overlayText={arrangerLegalName}
                />
            );
        },
        sortingField: PipelineColumn.arranger,
    }, {
        columnKey: PipelineColumn.transactionType,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.transactionType],
        renderColumnContent: transaction => startCase(transaction.type),
        sortingField: PipelineColumn.transactionType,
    }, {
        columnKey: PipelineColumn.transactionStatus,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.transactionStatus],
        renderColumnContent: transaction => <StatusLabel status={transactionStatusTitles[transaction.status]} />,
        sortingField: PipelineColumn.transactionStatus,
    }, {
        columnKey: PipelineColumn.collateralType,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.collateralType],
        renderColumnContent: transaction =>
            formatUtils.formatCollateralType(transaction.collateralType),
        className: "data-list-cell-xs",
        sortingField: PipelineColumn.collateralType,
    }, {
        columnKey: PipelineColumn.pricingDate,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.pricingDate],
        renderColumnContent: transaction =>
            transaction.pricingDate
                ? moment(transaction.pricingDate).format(constants.dateFormat)
                : constants.emptyPlaceholder,
        className: "",
        sortingField: PipelineColumn.pricingDate,
    }, {
        columnKey: PipelineColumn.nonCallPeriodEndDays,
        renderColumnHeaderContent: () => (
            <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.nonCallPeriodEnd]}>
                <span className="nce-rie-text">{pipelineColumnLabels[PipelineColumn.nonCallPeriodEndYears]}</span>
                <IconSVG name="info" width={16} height={16} />
            </OnHoverTooltip>
        ),
        renderColumnContent: transaction =>
            dateTimeUtils.yrsLeftToDateFrom(transaction.nonCallPeriodEnd, transaction.closingDate, false) ||
            constants.emptyPlaceholder,
        sortingField: PipelineColumn.nonCallPeriodEndDays,
        headerClassName: 'data-list-cell-sm data-list-cell-nce-rie text-right',
        bodyClassName: 'data-list-cell-sm text-right popover-column',
    }, {
        columnKey: PipelineColumn.reinvestmentPeriodEndDays,
        renderColumnHeaderContent: () => (
            <OnHoverTooltip overlay={pipelineTooltipText[PipelineColumn.reinvestmentPeriodEnd]}>
                <span className="nce-rie-text">{pipelineColumnLabels[PipelineColumn.reinvestmentPeriodEndYears]}</span>
                <IconSVG name="info" width={16} height={16} />
            </OnHoverTooltip>
        ),
        renderColumnContent: transaction =>
            dateTimeUtils.yrsLeftToDateFrom(transaction.reinvestmentPeriodEnd, transaction.closingDate, false) ||
            constants.emptyPlaceholder,
        sortingField: PipelineColumn.reinvestmentPeriodEndDays,
        headerClassName: 'data-list-cell-sm data-list-cell-nce-rie text-right',
        bodyClassName: 'data-list-cell-sm text-right popover-column',
    },

    //Default View Columns
    ...(viewType === KMatrixViewType.Default ? [
        createKMatrixColumn(Rating.AAAX, 'guidance', 'DM', "data-list-cell-lg data-list-cell-pre data-list-cell-border-left", true, true, dmTooltip),
        createKMatrixColumn(Rating.AAA, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.AAAJR, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.AA, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.A, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.BBB, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.BBBJR, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.BB, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.B, 'guidance', undefined, "data-list-cell-lg data-list-cell-pre", true, true),
        createKMatrixColumn(Rating.AAAX, 'parSubordination', 'C/E, %', "data-list-cell data-list-cell-border-left", true),
        createKMatrixColumn(Rating.AAA, 'parSubordination', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.AAAJR, 'parSubordination', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.AA, 'parSubordination', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.A, 'parSubordination', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.BBB, 'parSubordination', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.BBBJR, 'parSubordination', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.BB, 'parSubordination', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.B, 'parSubordination', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.AAAX, 'mvoc', 'MVOC, %', "data-list-cell data-list-cell-border-left", true),
        createKMatrixColumn(Rating.AAA, 'mvoc', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.AAAJR, 'mvoc', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.AA, 'mvoc', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.A, 'mvoc', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.BBB, 'mvoc', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.BBBJR, 'mvoc', undefined, "data-list-cell-xs padding-l-0", true),
        createKMatrixColumn(Rating.BB, 'mvoc', undefined, "data-list-cell-xxs-02 padding-l-0", true),
        createKMatrixColumn(Rating.B, 'mvoc', undefined, "data-list-cell-xxs-02 padding-l-0", true),
    ] : []),

    // Refi/Reset View Columns
    ...(viewType === KMatrixViewType.MVOC ? [Rating.AAAX, Rating.AAA, Rating.AA, Rating.A, Rating.BBB, Rating.BBBJR, Rating.BB, Rating.B].map(r => ([
        createKMatrixColumn(r, 'guidance', 'DM', "data-list-cell-lg data-list-cell-pre data-list-cell-border-left", true, true, dmTooltip, renderColumnHeaderContentRatingFirst),
        createKMatrixColumn(r, 'mvoc', 'MVOC, %', "data-list-cell", true, false, undefined, renderColumnHeaderContentWithoutRating),
    ])).flat() : []),

    // New Issue View Columns
    ...(viewType === KMatrixViewType.CE ? [Rating.AAAX, Rating.AAA, Rating.AA, Rating.A, Rating.BBB, Rating.BBBJR, Rating.BB, Rating.B].map(r => ([
        createKMatrixColumn(r, 'guidance', 'DM', "data-list-cell-lg data-list-cell-pre data-list-cell-border-left", true, true, dmTooltip, renderColumnHeaderContentRatingFirst),
        createKMatrixColumn(r, 'parSubordination', 'C/E, %', "data-list-cell", true, false, undefined, renderColumnHeaderContentWithoutRating),
    ])).flat() : []),

    {
        columnKey: PipelineColumn.was,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.was],
        renderColumnContent: (transaction) => (
            <ImSubscriptionValue>
                {
                    formatUtils.formatDecimal(transaction.was)
                    || constants.emptyPlaceholder
                }
            </ImSubscriptionValue>
        ),
        headerClassName: 'data-list-cell-xs data-list-cell-was data-list-cell-border-left text-right',
        bodyClassName: 'data-list-cell-xs data-list-cell-border-left text-right',
        sortingField: PipelineColumn.was,
    },
    {
        columnKey: PipelineColumn.warf,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.warf],
        renderColumnContent: ({ warf }) => (
            <ImSubscriptionValue>
                {warf ? moneyUtils.money(warf, true) : constants.emptyPlaceholder}
            </ImSubscriptionValue>
        ),
        headerClassName: 'text-right',
        bodyClassName: 'text-right',
        sortingField: PipelineColumn.warf,
    },
    {
        columnKey: PipelineColumn.ds,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.ds],
        renderColumnContent: (transaction) => (
            <ImSubscriptionValue>
                {
                    formatUtils.formatDecimal(transaction.ds)
                    || constants.emptyPlaceholder
                }
            </ImSubscriptionValue>
        ),
        headerClassName: 'data-list-cell-xxs-02 text-right',
        bodyClassName: 'data-list-cell-xxs-02 text-right',
        sortingField: PipelineColumn.ds,
    },
    {
        columnKey: PipelineColumn.purchasePrice,
        renderColumnHeaderContent: () => pipelineColumnLabels[PipelineColumn.purchasePrice],
        renderColumnContent: (transaction) =>
            formatUtils.formatDecimal(transaction.purchasePrice) || constants.emptyPlaceholder,
        headerClassName: 'data-list-cell-xxs-02 data-list-cell-pp data-list-cell-pp-last text-right',
        bodyClassName: 'data-list-cell-xxs-02 data-list-cell-pp-last text-right',
        sortingField: PipelineColumn.purchasePrice,
    },
    createKMatrixColumn(Rating.AAAX, 'rating', 'Rtg (M/S/F/K/D)', "data-list-cell-md data-list-cell-border-left", false, true),
    createKMatrixColumn(Rating.AAA, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.AAAJR, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.AA, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.A, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.BBB, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.BBBJR, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.BB, 'rating', undefined, "data-list-cell-sm-02", false, true),
    createKMatrixColumn(Rating.B, 'rating', undefined, "data-list-cell-sm-02", false, true),
    ];
}

function getKMatrixColumnContent(
    transactionStatus: TransactionStatus,
    transactionClasses: OriginatingTransactionClass[] | undefined,
    title: string,
    label: string,
    className: string,
    showTooltip: boolean,
    getTransactionClassProperty: (transactionClass: OriginatingTransactionClass, transactionStatus?: TransactionStatus) => string | number | null | undefined,
    getTooltipValue?: (transactionClass: OriginatingTransactionClass, transactionStatus?: TransactionStatus) => string | number | null | undefined,
    currencyCode?: Currency,
) {
    if (!transactionClasses?.length) {
        return <NotApplicable />;
    }

    const [firstTransactionClass, ...restClasses] = transactionClasses;
    const value = getTransactionClassProperty(firstTransactionClass, transactionStatus);

    const tooltipValue = getTooltipValue ? getTooltipValue(firstTransactionClass, transactionStatus) : value;

    if (!restClasses.length || !value) {
        return (value && value !== constants.emptyPlaceholder) ? (
            <OnHoverTooltip overlay={tooltipValue} disabled={!showTooltip} overlayClassName="kp-tooltip-pre">
                {value}
            </OnHoverTooltip>
        ) : (
            <OnHoverTooltip overlay="No data available">
                {constants.emptyPlaceholder}
            </OnHoverTooltip>
        );
    }

    return (
        <Popover
            title={title}
            className={className}
            label={
                <OnHoverTooltip overlay={label}>
                    <span className="name-tag">{label}</span>
                </OnHoverTooltip>
            }
            actionButton={
                <OnHoverTooltip overlay={tooltipValue} disabled={!showTooltip} overlayClassName="kp-tooltip-pre">
                    <button className="pseudo-link text-ellipsis">{value}</button>
                </OnHoverTooltip>
            }
        >
            {transactionClasses.map(tc => (
                <div key={tc.name}>
                    {`Class ${tc.name} (${moneyUtils.getCurrencySymbol(currencyCode)}${tc.balance != null && numericUtils.isNumber(tc.balance)
                        ? moneyUtils.money(tc.balance)
                        : constants.emptyPlaceholder
                        }): ${getTooltipValue ? getTooltipValue(tc, transactionStatus) : getTransactionClassProperty(tc, transactionStatus) ?? constants.emptyPlaceholder}`}
                </div>
            ))}
        </Popover>
    );
}

function renderColumnHeaderContentRatingFirst(headerText?: string, infoText?: string, ratingLabel?: string) {
    return (
        <div>
            <b>{ratingLabel}</b>
            <br />
            {headerText}
            {!!infoText && (
                <OnHoverTooltip overlay={infoText}>
                    <IconSVG name="info" width={16} height={16} />
                </OnHoverTooltip>
            )}
        </div>
    );
}

function renderColumnHeaderContentWithoutRating(headerText?: string, infoText?: string) {
    return (
        <div>
            {headerText}
            {!!infoText && (
                <OnHoverTooltip overlay={infoText}>
                    <IconSVG name="info" width={16} height={16} />
                </OnHoverTooltip>
            )}
        </div>
    );
}

function renderColumnHeaderContentDefault(headerText?: string, infoText?: string, ratingLabel?: string) {
    return (
        <div>
            {headerText && <b>{headerText}</b>}
            {!!infoText && (
                <OnHoverTooltip overlay={infoText}>
                    <IconSVG name="info" width={16} height={16} />
                </OnHoverTooltip>
            )}
            <br />
            {ratingLabel}
        </div>
    );
}

function createKMatrixColumn(
    rating: Rating,
    columnType: keyof typeof kMatrixValuesColumnsConfigs,
    headerText?: string,
    className?: string,
    checkSubscription?: boolean,
    showTooltip?: boolean,
    infoText?: string,
    renderColumnHeaderContent: (headerText?: string, infoText?: string, ratingLabel?: string) => React.JSX.Element = renderColumnHeaderContentDefault
): IColumnDefinition<KMatrix> {
    const config = kMatrixValuesColumnsConfigs[columnType];

    const ratingLabel = RatingsWithLabels.find(r => r.value === rating)?.text || rating;

    return {
        columnKey: `${columnType}-${rating}`,
        renderColumnHeaderContent: () => renderColumnHeaderContent(headerText, infoText, ratingLabel),
        renderColumnContent: transaction => {
            const transactionClasses = getKMatrixClassValue(transaction, rating);

            const content = getKMatrixColumnContent(
                transaction.status,
                transactionClasses,
                config.title,
                `${transaction.dealLegalName} / ${ratingLabel}`,
                'k-matrix-details-popover',
                !!showTooltip,
                config.getValue,
                config.getTooltipValue,
                transaction.currency?.code as Currency,
            );

            return (
                <ImSubscriptionValue overrideRequiredFeatures={!checkSubscription}>
                    {content}
                </ImSubscriptionValue>
            );
        },
        className,
        sortingField: `${columnType}-${rating}`,
    };
}


export const useKMatrixColumns = (viewType: KMatrixViewType) => {
    return useMemo(() =>
        getKMatrixColumns(viewType).map(column => new ColumnBuilder(column)),
        [viewType]
    );
};
